import { datadogRum } from '@datadog/browser-rum';
import { AuthUser } from '@shared/signup/models';
import { LocalStorageService } from '@tablesready/frontend-storage';
import {
  accountAgeDays,
  isNewAccount,
  NEW_ACCOUNT_CREATED_AT_STORAGE_KEY,
  NEW_ACCOUNT_REPLAY_DAYS,
} from './new-account-rum';

type MarkerStorage = Pick<LocalStorageService, 'get' | 'remove' | 'set'>;
interface RumApi {
  setUserProperty: typeof datadogRum.setUserProperty;
  startSessionReplayRecording: typeof datadogRum.startSessionReplayRecording;
  stopSession: typeof datadogRum.stopSession;
  stopSessionReplayRecording: typeof datadogRum.stopSessionReplayRecording;
}

/**
 * Keeps one signed-in layout's RUM in step with its account: the localStorage marker that
 * AppComponent reads at RUM init, `@usr.newAccount`/`@usr.accountAgeDays`, and forced replay
 * in the account's first days. Call `sync` on every location load and periodically: a session
 * renewed after 15 minutes idle or the 4-hour cap is sampled afresh, and the windows end while
 * the app stays open.
 */
export class NewAccountRumTracker {
  private replayForced = false;
  private newAccount?: boolean;
  private ageDays?: number | null;

  constructor(
    private readonly storage: MarkerStorage,
    private readonly rum: RumApi = datadogRum,
  ) {}

  /**
   * @param active signed in and this tab not locked. A stale tab must never touch the shared
   * Datadog session or the marker: another tab may have logged out and started a new one.
   */
  sync(user: AuthUser | null | undefined, active: boolean, now = Date.now()): void {
    if (!active) {
      this.stopLocalForcedReplay();

      return;
    }

    if (!user) {
      return;
    }

    // Users with no location get no account attributes (see AuthService.updateDataLayer)
    const createdAt = user.location ? user.account?.createdAt : undefined;
    const country = user.location?.country;
    const newAccount = isNewAccount(createdAt, country, now);

    this.rememberNewAccount(newAccount ? createdAt ?? null : null);
    this.refreshUserProperties(newAccount, createdAt ? accountAgeDays(createdAt, now) : null);
    this.syncReplay(isNewAccount(createdAt, country, now, NEW_ACCOUNT_REPLAY_DAYS));
  }

  private rememberNewAccount(createdAt: number | null): void {
    // Written only on change: every write fires a storage event in the other tabs
    if (this.storage.get<number>(NEW_ACCOUNT_CREATED_AT_STORAGE_KEY) === createdAt) {
      return;
    }

    if (createdAt) {
      this.storage.set(NEW_ACCOUNT_CREATED_AT_STORAGE_KEY, createdAt);
    } else {
      this.storage.remove(NEW_ACCOUNT_CREATED_AT_STORAGE_KEY);
    }
  }

  // Keeps the retention filter from matching once the account ages out in an open app
  private refreshUserProperties(newAccount: boolean, ageDays: number | null): void {
    if (newAccount !== this.newAccount) {
      this.rum.setUserProperty('newAccount', newAccount);
      this.newAccount = newAccount;
    }

    if (ageDays !== this.ageDays) {
      this.rum.setUserProperty('accountAgeDays', ageDays);
      this.ageDays = ageDays;
    }
  }

  private syncReplay(forceReplay: boolean): void {
    if (forceReplay) {
      this.rum.startSessionReplayRecording({ force: true });
    } else if (this.replayForced) {
      // The forced flag is stored on the session and a stopped recorder never restarts by
      // itself, so end the session: the next one is sampled for replay at the normal rate
      this.rum.stopSession();
    }

    this.replayForced = forceReplay;
  }

  private stopLocalForcedReplay(): void {
    if (this.replayForced) {
      this.rum.stopSessionReplayRecording();
      this.replayForced = false;
    }
  }
}
